/**
 * QuestFormCells — мелкие общие блоки экрана «Квесты и слоты» (и «Спецверсии»
 * в QS-4): ActionPair (стандарт 28.07 — обе кнопки одной геометрии, «✓» 12px
 * только на confirm), LpTip (long-press 450мс подсказка, автоскрытие 3200мс),
 * DualCell «Игра·Пауза» (NumPad int, long-press-подсказка Паузы), NumCell
 * (te-numbtn → NumPad), CollSec (сворачиваемая секция с шевроном), IntegBlock
 * (МК/Виджет: тап по строке — копирование в буфер + флэш «скопировано» 800мс).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { NumPad } from "../../components/shared";
import cfg from "./config.module.css";
import css from "./quests.module.css";

/** Δ длительности спецверсии: «Δ+30» / «Δ−15» (минус — U+2212, эталон js:46). */
export const fmtD = (d: number) => `Δ${d >= 0 ? "+" : "−"}${Math.abs(d)}`;

export const Chev = ({ open }: { open: boolean }) => (
  <span className={`${css.chev} ${open ? css.chevOpen : ""}`}>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M9 5l7 7-7 7" />
    </svg>
  </span>
);

/** Пара Подтвердить/Отменить — единый стандарт (UNIBOOKING-DESIGN-CONTEXT 28.07).
 *  03.10: переехала в components/shared/ActionPair.tsx (кадры), здесь — реэкспорт. */
export { ActionPair } from "../../components/shared/ActionPair";

/** Long-press подсказка (эталон bindTip js:129-132): зажать слово → tipbox. */
export function LpTip({ text, children }: { text: ReactNode; children: ReactNode }) {
  const [tip, setTip] = useState<DOMRect | null>(null);
  const [on, setOn] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const lpT = useRef(0);
  const hideT = useRef(0);

  useEffect(() => {
    if (!tip) return setOn(false);
    const raf = requestAnimationFrame(() => setOn(true));
    hideT.current = window.setTimeout(() => setTip(null), 3200);
    const down = (e: PointerEvent) => {
      if (!(e.target instanceof Node && boxRef.current?.contains(e.target))) setTip(null);
    };
    document.addEventListener("pointerdown", down, true);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(hideT.current);
      document.removeEventListener("pointerdown", down, true);
    };
  }, [tip]);

  useEffect(() => () => { window.clearTimeout(lpT.current); window.clearTimeout(hideT.current); }, []);

  const pos = (): { left: number; top: number } => {
    if (!tip) return { left: 0, top: 0 };
    const w = 230, h = 40; // оценка; клампы эталона showTip (js:124-125)
    const left = Math.max(6, Math.min(tip.left, window.innerWidth - w - 6));
    let top = tip.top - h - 6;
    if (top < 6) top = tip.bottom + 6;
    return { left, top };
  };

  return (
    <>
      <span
        className={css.tiphint}
        onPointerDown={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          lpT.current = window.setTimeout(() => setTip(r), 450);
        }}
        onPointerUp={() => window.clearTimeout(lpT.current)}
        onPointerLeave={() => window.clearTimeout(lpT.current)}
        onPointerCancel={() => window.clearTimeout(lpT.current)}
        onContextMenu={(e) => e.preventDefault()}
      >
        {children}
      </span>
      {tip
        ? createPortal(
            <div ref={boxRef} className={`${css.tipbox} ${on ? css.on : ""}`} style={pos()}>{text}</div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Ячейка te-numbtn → NumPad (эталон numCell js:163-167). */
export function NumCell(p: {
  label: ReactNode;
  value: ReactNode;
  np: { label: string; mode: "money" | "int"; unit?: string; max?: number; value: number; presets?: number[] };
  onCommit: (v: number) => void;
  center?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${cfg.cell} ${css.cellInline} ${css.cellNowrap} ${p.center ? css.numCell : ""}`}>
      <span className={cfg.klbl}>{p.label}</span>
      <button type="button" className={cfg.teNumbtn} onClick={() => setOpen(true)}>{p.value}</button>
      <NumPad open={open} {...p.np} onCommit={p.onCommit} onClose={() => setOpen(false)} />
    </div>
  );
}

/** Dual-ячейка Игра·Пауза (эталон dualCell js:373-381): NumPad int, тип у Паузы;
 *  span2 — на обе колонки cfg-grid (спецверсии, js:457). */
export function DualCell(p: {
  dur: number; reset: number;
  onDur: (v: number) => void; onReset: (v: number) => void;
  span2?: boolean;
}) {
  const [np, setNp] = useState<"dur" | "reset" | null>(null);
  const half = (lbl: ReactNode, v: number, open: () => void) => (
    <div className={css.gap2} style={{ minWidth: 0 }}>
      <span className={cfg.klbl}>{lbl}</span>
      <button type="button" className={cfg.teNumbtn} style={{ alignSelf: "flex-start" }} onClick={open}>
        <span className={css.num}>{v}</span>&nbsp;мин
      </button>
    </div>
  );
  return (
    <div
      className={`${cfg.cell} ${css.cellInline} ${css.cellNowrap}`}
      style={{ gap: 12, ...(p.span2 ? { gridColumn: "span 2" } : null) }}
    >
      {half("Игра", p.dur, () => setNp("dur"))}
      {half(
        <LpTip text="Пауза — мин между играми, перезарядка комнаты">Пауза</LpTip>,
        p.reset,
        () => setNp("reset"),
      )}
      <NumPad
        open={np !== null}
        label={np === "dur" ? "Длительность игры" : "Пауза — перезарядка"}
        mode="int"
        unit="мин"
        max={np === "dur" ? 600 : 120}
        value={np === "dur" ? p.dur : p.reset}
        onCommit={(v) => (np === "dur" ? p.onDur(v || p.dur) : p.onReset(v))}
        onClose={() => setNp(null)}
      />
    </div>
  );
}

/** Сворачиваемая секция «Заголовок (N)» + шеврон (эталон collSec js:158-162). */
export function CollSec(p: { title: string; count: ReactNode; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <div className={css.gap2}>
      <div className={css.collHead} onClick={p.onToggle}>
        <span className={cfg.klbl}>{p.title} (<span className={css.num}>{p.count}</span>)</span>
        <Chev open={p.open} />
      </div>
      {p.open ? p.children : null}
    </div>
  );
}

/** Интеграция МК/Виджет: сворачиваемый блок, тап по строке — копирование. */
export function IntegBlock(p: {
  variant: "mk" | "wg";
  title: string;
  icon: ReactNode;
  open: boolean;
  onToggle: () => void;
  rows: { pre: string; txt: string }[];
  /** Доп-контент под строками (поле mk_id и т.п., 23.08). */
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(-1);
  const t = useRef(0);
  useEffect(() => () => window.clearTimeout(t.current), []);
  const copy = (i: number, txt: string) => {
    const done = () => {
      setCopied(i);
      window.clearTimeout(t.current);
      t.current = window.setTimeout(() => setCopied(-1), 800);
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(txt).then(done, done);
    else done();
  };
  return (
    <div className={`${css.mk} ${p.variant === "wg" ? css.wg : ""}`}>
      <div className={`${cfg.secRow} ${css.mkH}`} onClick={p.onToggle}>
        {p.icon}
        <h4 className={`${cfg.secT} ${css.pu}`} style={{ color: p.variant === "wg" ? "var(--cyan)" : undefined }}>
          {p.title}
        </h4>
        <span
          className={css.mkCar}
          style={{ transform: p.open ? "rotate(90deg)" : undefined, color: `var(--${p.variant === "wg" ? "cyan" : "purple"})` }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
      {p.open
        ? p.rows.map((r, i) => (
            <div key={i} className={css.u} title="Скопировать" onClick={() => copy(i, r.txt)}>
              <b>{r.pre}</b> {r.txt}
              {copied === i ? <span className={css.copied}> скопировано</span> : null}
            </div>
          ))
        : null}
      {p.open ? p.children : null}
    </div>
  );
}
